/**
 * Pure utility functions for analytics. No framework dependencies.
 */

import { DeviceType, EventData, ContextInfo } from '../models/event-types';
import { PLACEHOLDER_STRINGS, PRODUCT_EVENT_TYPES, ECOMMERCE_EVENT_TYPES } from '../constants/analytics-constants';

/** Generate a unique event ID. */
export function generateEventId(): string {
  return `evt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/** Generate a unique session ID. */
export function generateSessionId(): string {
  return `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/** Detect device type from user agent string. */
export function getDeviceType(userAgent: string): DeviceType {
  if (/tablet|ipad|playbook|silk/i.test(userAgent)) {
    return 'tablet';
  }
  if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(userAgent)) {
    return 'mobile';
  }
  return 'desktop';
}

/** Format a timestamp as ISO string. */
export function formatTimestamp(timestamp?: number): string {
  return new Date(timestamp || Date.now()).toISOString();
}

/** Check if a value is a plain record. */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Check if a string value is a known placeholder that should be rejected. */
export function isPlaceholder(value: string | undefined): boolean {
  if (!value) return true;
  return PLACEHOLDER_STRINGS.has(value.toLowerCase());
}

/** Check if an event type requires product-specific validation. */
export function isProductEvent(eventType: string): boolean {
  return PRODUCT_EVENT_TYPES.includes(eventType as never);
}

/** Check if an event type is an ecommerce event. */
export function isEcommerceEvent(eventType: string): boolean {
  return ECOMMERCE_EVENT_TYPES.includes(eventType as never);
}

/** Extract SKU from eventData, supporting multiple key formats. */
export function extractSku(eventData: EventData | Record<string, unknown> | undefined): string | undefined {
  if (!eventData || !isRecord(eventData)) return undefined;
  const skuKeys = ['sku', 'productId', 'product_id'];
  for (const k of skuKeys) {
    const v = eventData[k];
    if (typeof v === 'string' && v.trim() !== '') return v.trim();
  }
  return undefined;
}

/** Extract product storeCode from eventData. */
export function extractProductStoreCode(eventData: EventData | Record<string, unknown> | undefined): string | undefined {
  if (!eventData || !isRecord(eventData)) return undefined;
  const storeVal = eventData['storeCode'];
  if (typeof storeVal === 'string' && storeVal.trim() !== '') return storeVal.trim();
  return undefined;
}

/** Extract storeCode from contextInfo (user's nearest store). */
export function extractNearestStoreCode(contextInfo?: ContextInfo | Record<string, unknown>): string | undefined {
  if (!contextInfo || !isRecord(contextInfo)) return undefined;
  const candidates = ['storeCode', 'store_code', 'store-code', 'storecode'];
  for (const key of candidates) {
    const v = contextInfo[key];
    if (typeof v === 'string' && v.trim() !== '') return v;
  }
  return undefined;
}

/** Create a composite product identifier for multi-store scenarios. */
export function createCompositeProductId(sku: string, storeCode: string): string {
  if (!sku || !storeCode) return sku || '';
  return `${sku}@${storeCode}`;
}

/** Parse a composite product identifier back to components. */
export function parseCompositeProductId(compositeId: string): { sku: string; storeCode: string } {
  if (!compositeId || !compositeId.includes('@')) {
    return { sku: compositeId || '', storeCode: '' };
  }
  const [sku, storeCode] = compositeId.split('@', 2);
  return { sku: sku || '', storeCode: storeCode || '' };
}
